// ===========================================
// Offline Indicator Component
// Shows a banner when the user is offline
// ===========================================

import { useState, useEffect } from 'react';
import {
  Snackbar,
  Alert,
  Slide,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import type { SlideProps } from '@mui/material/Slide';
import {
  WifiOff as WifiOffIcon,
  Wifi as WifiIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="down" />;
}

interface OfflineIndicatorProps {
  /** Show a success message when coming back online */
  showOnlineConfirmation?: boolean;
  /** Auto-hide the online confirmation after this many milliseconds */
  onlineConfirmationDuration?: number;
}

export default function OfflineIndicator({
  showOnlineConfirmation = true,
  onlineConfirmationDuration = 3000,
}: OfflineIndicatorProps) {
  const { isOnline, isOffline } = useOnlineStatus();
  const [showOffline, setShowOffline] = useState(false);
  const [showOnline, setShowOnline] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    if (isOffline) {
      setShowOffline(true);
      setShowOnline(false);
      setWasOffline(true);
    } else if (isOnline && wasOffline) {
      setShowOffline(false);
      if (showOnlineConfirmation) {
        setShowOnline(true);
        // Auto-hide after duration
        const timeout = setTimeout(() => {
          setShowOnline(false);
        }, onlineConfirmationDuration);
        return () => clearTimeout(timeout);
      }
    }
    return undefined;
  }, [isOnline, isOffline, wasOffline, showOnlineConfirmation, onlineConfirmationDuration]);

  const handleCloseOnline = () => {
    setShowOnline(false);
  };

  return (
    <>
      {/* Offline Banner */}
      <Snackbar
        open={showOffline}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        TransitionComponent={SlideTransition}
        sx={{
          top: { xs: 0, sm: 8 },
          '& .MuiSnackbarContent-root': {
            minWidth: 'auto',
          },
        }}
      >
        <Alert
          severity="warning"
          variant="filled"
          icon={<WifiOffIcon />}
          sx={{
            width: '100%',
            maxWidth: 500,
            alignItems: 'center',
            backgroundColor: '#ed6c02', // MUI warning color
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              You're offline
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              - Some features may be unavailable
            </Typography>
          </Box>
        </Alert>
      </Snackbar>

      {/* Online Confirmation */}
      <Snackbar
        open={showOnline}
        autoHideDuration={onlineConfirmationDuration}
        onClose={handleCloseOnline}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        TransitionComponent={SlideTransition}
        sx={{
          top: { xs: 0, sm: 8 },
        }}
      >
        <Alert
          severity="success"
          variant="filled"
          icon={<WifiIcon />}
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={handleCloseOnline}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
          sx={{
            width: '100%',
            maxWidth: 500,
            alignItems: 'center',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            You're back online
          </Typography>
        </Alert>
      </Snackbar>
    </>
  );
}
